/**
 * Invoices API service. Mirrors backend src/services/invoices.ts shapes.
 */
import { apiClient } from './api';

export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';

export interface InvoiceStatusHistoryEntry {
  from_status: string | null;
  to_status: InvoiceStatus;
  changed_by_user_id: number | null;
  changed_at: string;
  reason: string | null;
}

export interface Invoice {
  id: number;
  order_id: number;
  invoice_number: string;
  invoice_date: string;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  status: InvoiceStatus;
  order_data?: Record<string, unknown>;
  status_history?: InvoiceStatusHistoryEntry[];
  created_by_user_id?: number;
  created_at: string;
  updated_at: string;
  // present on customer-self list
  order_status?: string;
}

export interface InvoiceSummary {
  total: number;
  draft: number;
  sent: number;
  paid: number;
  overdue: number;
  total_billed: number;
  outstanding: number;
}

export interface ListInvoicesFilters {
  status?: InvoiceStatus;
  customer_id?: number;
  from_date?: string;
  to_date?: string;
  limit?: number;
  offset?: number;
}

function normalize(raw: any): { data: Invoice[]; total: number; limit: number; offset: number } {
  const inner = raw?.data?.invoices !== undefined ? raw.data : raw;
  return {
    data: inner.invoices ?? [],
    total: inner.total ?? 0,
    limit: inner.limit ?? 20,
    offset: inner.offset ?? 0,
  };
}

export async function listInvoices(filters: ListInvoicesFilters = {}): Promise<{
  data: Invoice[];
  total: number;
  limit: number;
  offset: number;
}> {
  const raw = await apiClient.get<any>('/invoices', filters as Record<string, any>);
  return normalize(raw);
}

export async function getInvoice(id: number | string): Promise<Invoice> {
  return apiClient.get<Invoice>(`/invoices/${id}`);
}

export async function getInvoicesSummary(): Promise<InvoiceSummary> {
  return apiClient.get<InvoiceSummary>('/invoices/summary');
}

export async function createInvoiceFromOrder(orderId: number | string): Promise<Invoice> {
  return apiClient.post<Invoice>(`/orders/${orderId}/invoice`, {});
}

export async function updateInvoiceStatus(
  id: number | string,
  status: InvoiceStatus,
  reason?: string
): Promise<Invoice> {
  return apiClient.put<Invoice>(`/invoices/${id}/status`, { status, reason });
}

export async function listMyCustomerInvoices(filters: ListInvoicesFilters = {}): Promise<{
  data: Invoice[];
  total: number;
  limit: number;
  offset: number;
}> {
  const raw = await apiClient.get<any>('/customers/me/invoices', filters as Record<string, any>);
  return normalize(raw);
}

/**
 * VP-CUST-005: real invoice PDF, scoped to the invoice's own customer.
 * Backend: GET /invoices/:id/pdf (Batch A5).
 */
export async function downloadInvoicePdf(id: number | string): Promise<Blob> {
  return apiClient.downloadBlob(`/invoices/${id}/pdf`);
}
